// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Material UI Components
import { Grid, Paper } from "@mui/material";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import { Alert, AlertTitle } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PolicyIcon from "@mui/icons-material/Policy";

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import AuditsByWorkloadSource from "../components/reports/AuditsByWorkloadSource";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import ScheduledThreatJobList from "../components/reports/ScheduledThreatJobsList";
import ScheduledThreatJobHistoryList from "../components/reports/ScheduledThreatJobHistoryList";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { formatAuditJobRunTimeDateServerTimeToGMT } from "../utilities/common-date-utils";
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import {
    ROLE_CODE_TENANTADMINISTRATOR,
    ROLE_CODE_TENANTREPORTREADER,
} from "../constants/constants-roles";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

const theBreadcrumbPath = [
    { name: "Home", link: "/" },
    { name: "Reports", link: "/Reports/ReportsHome" },
    { name: "Threat Intelligence", link: "/Reports/ThreatIntelligence" },
];

const theSectionTitle = "Threat Intelligence (Detect Threats) Dashboard";
const theSectionDesc =
    "Harness cloud-based threat detection heuristics for proactive security monitoring to help you identify security breaches, incidents, concerns and enable risk mitigation in your M365 environment.  Below are built-in reports that you can use to assist in detecting both common external and internal threats.";
const isAdminPage = false;

// Common styles for report links inside tab panels (same as other report pages)
const linkListSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
};

const reportLinkSx = {
    display: "block",
    width: "100%",
    textAlign: "left",
    whiteSpace: "normal",
    marginBottom: 0.5,
};

function ReportsThreatIntelligence(props) {
    const { user, openCompanyNag, setOpenCompanyNag } = props;
    const queryParams = new URLSearchParams(window.location.search);

    // Component Constants
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [accessDenied, setAccessDenied] = useState(false);
    const [selectedTenantOption, setSelectedTenantOption] = useState('');

    const [totalAuditRecords, setTotalAuditRecords] = useState(null);
    const [totalSPRecords, setTotalSPRecords] = useState(null);
    const [totalExchangeRecords, setTotalExchangeRecords] = useState(null);
    const [totalEntraRecords, setTotalEntraRecords] = useState(null);
    const [totalTeamsRecords, setTotalTeamsRecords] = useState(null);
    const [loadingTotalAuditRecords, setLoadingTotalAuditRecords] = useState(false);
    const [totalAuditRecordsLoaded, setTotalAuditRecordsLoaded] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();


    const [tabValue, setTabValue] = useState(0);

    // Component Functions
    async function fetchTenantHealthStats(theTenantId) {
        setLoading(true);
        try {
            const response2 = await getHealthStatsByTenantId(theTenantId, 8, user);
            if (response2) {
                setTenantHealthStats(response2);
            }
        } catch (e) {
            console.log("ERROR: ReportThreatIntelligence.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        } finally {
            setLoading(false);
        }
    }

    // Load the total audit records on demand.
    async function fetchTotalAuditRecordsOnDemand() {
        if (!user?.tenantId || loadingTotalAuditRecords || totalAuditRecordsLoaded) {
            return;
        }

        setLoadingTotalAuditRecords(true);

        try {
            // Heavier on-demand total counts load.
            const response = await getHealthStatsByTenantId(user.tenantId, 11, user);
            if (response) {
                const spTotal = response.totalSharePointAuditRecords;
                const exchangeTotal = response.totalExchangeAuditRecords;
                const entraTotal = response.totalEntraIDAuditRecords;
                const teamsTotal = response.totalTeamsAuditRecords;
                const total = spTotal + exchangeTotal + entraTotal + teamsTotal;
                setTotalAuditRecords(total);
                setTotalSPRecords(spTotal);
                setTotalExchangeRecords(exchangeTotal);
                setTotalEntraRecords(entraTotal);
                setTotalTeamsRecords(teamsTotal);
                setTotalAuditRecordsLoaded(true);
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoadingTotalAuditRecords(false);
        }
    }


    useEffect(() => {
        async function fetchCompanyData() {
            try {
                if (queryParams.get("tab") && queryParams.get("tab").length > 0) {
                    setTabValue(Number(queryParams.get("tab")));
                }

                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }

                if (user && user.companyId) {
                    const response1 = await getCompanyByCompanyId(
                        user.companyId,
                        setServiceIsDownError
                    );
                    if (response1) {

                        if (response1.tenantList && response1.tenantList.length > 0) {
                            setSelectedTenantOption(response1.tenantList[0]);  // Set to the first item in the list.
                        }
                        setCompany(response1);
                        fetchTenantHealthStats(user.tenantId);
                    }
                }
                setLoading(false);
            } catch (e) {
                console.log("ERROR: ReportThreatIntelligence.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            } finally {
                setLoading(false);
            }
        }

        fetchCompanyData();
    }, []);

    // Tab panel.
    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={linkListSx}>{children}</Box>
                    </Box>
                )}
            </div>
        );
    }

    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            "aria-controls": `simple-tabpanel-${index}`,
        };
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        fetchTenantHealthStats(event.target.value.id);
    };

    // Component UI
    if (loading) {
        console.log("Loading Data ....");
        return <ProgressBar message="Loading ..." loading={loading} />;
    }

    if (serviceIsDownError) {
        return (
            <>
                <ServiceIsDown />
            </>
        );
    }

    const sharePointPct = totalAuditRecords > 0 ? ((totalSPRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
    const exchangePct = totalAuditRecords > 0 ? ((totalExchangeRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
    const entraPct = totalAuditRecords > 0 ? ((totalEntraRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
    const teamsPct = totalAuditRecords > 0 ? ((totalTeamsRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";

    // A valid Tenant is chosen from the textfield selection drop down list.
    return (
        <>
            <CustomerPageScaffold
                breadcrumbPaths={theBreadcrumbPath}
                sectionTitle={theSectionTitle}
                sectionDescription={theSectionDesc}
                isAdminPage={isAdminPage}
                company={company}
                selectedTenant={selectedTenantOption}
                openCompanyNag={openCompanyNag}
                setOpenCompanyNag={setOpenCompanyNag}
                headerAdornment={<PolicyIcon sx={{ fontSize: '3rem' }} />}
            />

            <div>
                <Grid container spacing={2}>
                    {accessDenied && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <AccessDenied />
                        </Grid>
                    )}

                    {user.tenantId == 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <Alert severity="error">
                                <AlertTitle>
                                    Your Company has not yet completed the setup process or you have
                                    not yet specified a valid Tenant.
                                </AlertTitle>
                            </Alert>
                        </Grid>
                    )}

                    {tenantHealthStats.status === 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <TenantSetupRequired />
                        </Grid>
                    )}

                    {!accessDenied && user.tenantId > 0 && tenantHealthStats.status >= 1 ? (
                        <>
                            <Grid item xs={12} sx={{ mt: -2 }}>
                                <Grid container spacing={2}>
                                    <Grid item>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Last audit job run time */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                Audit data last updated on:{" "}
                                {formatAuditJobRunTimeDateServerTimeToGMT(
                                    tenantHealthStats.entraIDAuditLastSuccessfulRunTime
                                )}
                            </Grid>
                            
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <AuditsByWorkloadSource
                                        myTitle="Summary of Audits by Workload Source (last 24 hours)"
                                        totalSharePointAuditRecords={tenantHealthStats.totalSharePointAuditRecordsIn24Hours}
                                        totalExchangeAuditRecords={tenantHealthStats.totalExchangeAuditRecordsIn24Hours}
                                        totalAzureAdAuditRecords={tenantHealthStats.totalEntraIDAuditRecordsIn24Hours}
                                        totalTeamsAuditRecords={tenantHealthStats.totalTeamsAuditRecordsIn24Hours}
                                    />
                                </Paper>
                            </Grid>

                            { /* Display total audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 3 }}
                                        >
                                            Total Audit Records Stored
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            {!totalAuditRecordsLoaded && !loadingTotalAuditRecords && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={fetchTotalAuditRecordsOnDemand}
                                                >
                                                    Show total records
                                                </Button>
                                            )}

                                            {loadingTotalAuditRecords && (
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ fontSize: 15 }}
                                                >
                                                    Loading count...
                                                </Typography>
                                            )}

                                            {totalAuditRecordsLoaded && !loadingTotalAuditRecords && (
                                                <>
                                                    <Typography
                                                        component="div"
                                                        variant="h5"
                                                        sx={{ fontSize: 19, fontWeight: 800, mb: 2 }}
                                                    >
                                                        {totalAuditRecords?.toLocaleString()}
                                                    </Typography>

                                                    <Box sx={{ mt: 2, textAlign: 'left' }}>
                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(255, 99, 132)' }} />
                                                                <Typography variant="body2">
                                                                    SharePoint
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {totalSPRecords?.toLocaleString()} ({sharePointPct}%)
                                                            </Typography>
                                                        </Box>

                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(54, 162, 235)' }} />
                                                                <Typography variant="body2">
                                                                    Exchange
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {totalExchangeRecords?.toLocaleString()} ({exchangePct}%)
                                                            </Typography>
                                                        </Box>

                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(255, 205, 86)' }} />
                                                                <Typography variant="body2">
                                                                    Entra ID
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {totalEntraRecords?.toLocaleString()} ({entraPct}%)
                                                            </Typography>
                                                        </Box>

                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(18, 211, 74)' }} />
                                                                <Typography variant="body2">
                                                                    Teams
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {totalTeamsRecords?.toLocaleString()} ({teamsPct}%)
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* 
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 2,
                                        border: "1px solid lightgray",
                                        height: "100%",
                                        minWidth: 320,
                                    }}
                                >
                                    <AuditsByWorkloadSource
                                        totalSharePointAuditRecords={totalSharePointAuditRecords}
                                        totalExchangeAuditRecords={totalExchangeAuditRecords}
                                        totalAzureAdAuditRecords={totalAzureAdAuditRecords}
                                        totalTeamsAuditRecords={totalTeamsAuditRecords}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 2,
                                        border: "1px solid lightgray",
                                        height: "100%",
                                    }}
                                >
                                    <div style={{ height: "100%" }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, paddingBottom: 1 }}
                                        >
                                            Total Audit Records
                                        </Typography>
                                        <br />
                                        <br />
                                        <div style={{ textAlign: "center" }}>
                                            <Typography
                                                component="div"
                                                variant="h5"
                                                sx={{ fontSize: 19, fontWeight: "800" }}
                                            >
                                                {totalAuditRecords}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                </Paper>
                            </Grid>
                            */}


                            {/* Insights search (uses its own internal Grid layout) */}
                            <InsightsSearchBox colSpanVal={4} workloadName={""} />

                            {/* Tabs section */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%" }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            aria-label="Exchange Reports Tab"
                                        >
                                            <Tab label="Detect" {...a11yProps(0)} />
                                            <Tab label="Schedule" {...a11yProps(1)} />
                                            <Tab label="Monitor" {...a11yProps(2)} />
                                        </Tabs>
                                    </Box>

                                    {/* Detect */}
                                    <TabPanel value={tabValue} index={0}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                            }}
                                        >
                                            Report List: External Threats
                                        </Typography>

                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatEntraIDFailedLoginsInvalidPasswordReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins due to Invalid Password
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatEntraIDFailedLoginsFailedMFAReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Failed Logins due to MFA Related Failure
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatEntraIDSuccessfulLoginsDifferentIPsReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Flag Successful Logins with Different IP Addresses
                                            (unusual activity)
                                        </Link>

                                        <Box sx={{ mt: 2 }} />

                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                            }}
                                        >
                                            Report List: Insider Threats (potential malicious
                                            activity)
                                        </Typography>

                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatSharepointFileDownloadsReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Identify Bulk SharePoint File Downloads
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatSharepointFileAccessesReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Identify Bulk SharePoint File Accesses
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatSharepointFileOrFolderDeletionsReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Identify Bulk SharePoint File or Folder Deletions
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatSharepointPermsUpdateAddedToGroupReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Identify Bulk SharePoint Permissions Updates (AddToGroup)
                                        </Link>
                                        <Link
                                            sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/ThreatExchangeDeletedItemsReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            Identify Bulk Exchange Deleted Items
                                        </Link>
                                    </TabPanel>

                                    {/* Schedule */}
                                    <TabPanel value={tabValue} index={1}>
                                        {verifyUserHasTenantRolePermissions(
                                            user,
                                            ROLE_CODE_TENANTADMINISTRATOR
                                        ) ? (
                                            <>
                                                <ScheduledThreatJobList
                                                    user={user}
                                                />
                                                <br />
                                                <Button
                                                    variant="contained"
                                                    type="button"
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            `/Reports/ThreatIntelligence/ScheduleReport`
                                                        )
                                                    }
                                                >
                                                    Schedule New Threat Report
                                                </Button>
                                            </>
                                        ) : (
                                            <AccessDenied />
                                        )}
                                    </TabPanel>

                                    {/* Monitor */}
                                    <TabPanel value={tabValue} index={2}>
                                        {verifyUserHasTenantRolePermissions(
                                            user,
                                            ROLE_CODE_TENANTADMINISTRATOR
                                        ) ? (
                                            <ScheduledThreatJobHistoryList
                                                user={user}
                                            />
                                        ) : (
                                            <AccessDenied />
                                        )}
                                    </TabPanel>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <ProgressBar message="Loading ..." loading={loading} />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} sx={{ p: 2 }}>
                        <br />
                        Need a custom or new report? Provide us with your{" "}
                        <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                            Product Feedback
                        </Link>
                        .
                        <br />
                        * Unless otherwise specified as UTC, graphs on page are using your browser's
                        local timezone.
                    </Grid>
                </Grid>
            </div>
        </>
    );
}

export default ReportsThreatIntelligence;
